import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Modal,
  Drawer,
  Alert
} from '@mui/material';
import { plusIcon } from "../../../assets/images/index";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EditIcon, Close, Loader, downloadIcon, crossIcon, trashIcon, deleteIcon } from '../../../assets/images/index';
import Button from '../../../components/button';
import CustomDataGrid from '../../../components/data-grid';
import CustomTabs from '../../../components/custom-tabs';
import MathExpressionNew from '../../../components/common/mathjax';
import ExerciseSolution from '../../../components/exercise-solution';
import ExerciseQuestionList from '../../../components/exercise-question-list';
import Dropdown from '../../../components/dropdown/index';
import { SearchExerciseService } from '../../../services/search-exercise/search-exercise.service';
import ExerciseDetailsDrawer from '../../../components/exercise-details-drawer/ExerciseDetailsDrawer';
import { ExerciseQuestionsService } from '../../../services/exercise-questions/exercise-questions.service';
import { NewExerciseService } from '../../../services/new-exercises/new-exercises.service';
import { ChatService } from '../../../services/chat/chat.services';
import { showErrorToast, showSuccessToast } from '../../../components/Toaster/index';
import { scopeCSS } from '../../../utils/scopeCSS';
import { hebrewLetters } from '../../../configs/config';
import ConfirmationModal from '../../../components/confirmation-modal';
import { useLessonStore } from '../../../utils/lessonStore';
import './search-exercise.scss';

interface DropdownOption {
  value: string;
  label: string;
}

interface ExerciseChart {
  question_id: number;
  chart_image_base64: string;
}

interface ExerciseQuestion {
  id: number;
  question: string;
  chart_image_data?: string;
}

interface ExerciseRow {
  id: number;
  filename: string;
  status: string;
  upload_date: string;
  is_solution_approved: boolean;
  exerciseName: string;
}

interface ConfirmationData {
  title?: string;
  message: string;
  onConfirm: () => Promise<void> | void;
}

const SearchExercise: React.FC = () => {
  const { t } = useTranslation();
  const searchExerciseService = new SearchExerciseService();
  const newExerciseService = new NewExerciseService();
  const chatService = new ChatService();

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');

  const [rows, setRows] = useState<ExerciseRow[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);

  const [levelOptions, setLevelOptions] = useState<DropdownOption[]>([{ value: '', label: t('all') }]);
  const [difficultyOptions, setDifficultyOptions] = useState<DropdownOption[]>([{ value: '', label: t('all') }]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedExerciseId, setSelectedExerciseId] = useState<number | null>(null);
  const [openChartModal, setOpenChartModal] = useState<boolean>(false);
  const [chartImage, setChartImage] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState<boolean>(false);
  const [confirmationLoading, setConfirmationLoading] = useState<boolean>(false);
  const [confirmationData, setConfirmationData] = useState<ConfirmationData>({
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const {
    topicOptions: storeTopicOptions,
    isTopicsLoading,
    fetchTopics: storeFetchTopics,
  } = useLessonStore();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedImage(acceptedFiles[0]);
      setUploadError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024,
  });

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setUploadError('');
  };

  const handleSearchByImage = async (): Promise<void> => {
    if (!selectedImage) {
      setUploadError(t('pleaseSelectImage'));
      return;
    }

    setIsSearching(true);
    setUploadError('');
    setLoading(true);
    setPage(0);

    try {
      const response = await searchExerciseService.searchExerciseByImage(selectedImage, 1, pageSize);
      
      if (response && response.results) {
        const formatted: ExerciseRow[] = response.results.map((item: any) => ({
          id: item.id,
          filename: item.filename.replace(/\.docx$/i, ''),
          similarity_score: item.similarity_score,
          status: item.is_exercise_approved && item.is_solution_approved ? 'אושר' : 'לא אושר',
          upload_date: new Date(item.upload_date).toLocaleDateString('he-IL'),
          is_solution_approved: item.is_solution_approved,
          exerciseName: item.filename
        }));
        
        setRows(formatted);
        setRowCount(response.total);
        
        if (formatted.length === 0) {
          showErrorToast(t('noSimilarExercisesFound'));
        } else {
          showSuccessToast(t('foundSimilarExercises', { count: response.total }));
        }
      } else {
        setRows([]);
        setRowCount(0);
        showErrorToast(t('noSimilarExercisesFound'));
      }
    } catch (error) {
      console.error('Search by image failed:', error);
      setUploadError(t('searchByImageFailed'));
      setRows([]);
      setRowCount(0);
      showErrorToast(t('searchByImageFailed'));
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  const fetchSearchResults = async (): Promise<void> => {
    if (!selectedImage) return;
    
    setLoading(true);
    try {
      const response = await searchExerciseService.searchExerciseByImage(selectedImage, page + 1, pageSize);
      
      if (response && response.results) {
        const formatted: ExerciseRow[] = response.results.map((item: any) => ({
          id: item.id,
          filename: item.filename.replace(/\.docx$/i, ''),
          similarity_score: item.similarity_score,
          status: item.is_exercise_approved && item.is_solution_approved ? 'אושר' : 'לא אושר',
          upload_date: new Date(item.upload_date).toLocaleDateString('he-IL'),
          is_solution_approved: item.is_solution_approved,
          exerciseName: item.filename
        }));

        setRows(formatted);
        setRowCount(response.total);
      }
    } catch (error) {
      console.error('Failed to fetch search results:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedImage && rowCount > 0) {
      fetchSearchResults();
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchLevels();
    fetchTopics();
    fetchDifficulties();
  }, []);



  const fetchLevels = async (): Promise<void> => {
    try {
      const levels = await searchExerciseService.getLevels();
      const formatted: DropdownOption[] = levels.map((l: any) => ({ value: l.id.toString(), label: l.name }));
      setLevelOptions([{ value: '', label: t('all') }, ...formatted]);
    } catch (error) {
      console.error("Failed to fetch levels", error);
    }
  };

  const fetchTopics = async (): Promise<void> => {
    storeFetchTopics(searchExerciseService);
  };

  const topicOptions = React.useMemo(() => [{ value: '', label: t('all') }, ...storeTopicOptions], [storeTopicOptions, t]);


  const fetchDifficulties = async (): Promise<void> => {
    try {
      const difficulties = await searchExerciseService.getDifficulties();
      const formatted: DropdownOption[] = difficulties.map((d: any) => ({ value: d.id.toString(), label: d.name }));
      setDifficultyOptions([{ value: '', label: t('all') }, ...formatted]);
    } catch (error) {
      console.error("Failed to fetch difficulties", error);
    }
  };



  const handleDelete = async (row: ExerciseRow): Promise<void> => {
    try {
      const res = await searchExerciseService.deleteExerciseFromDB(row.id);
      if (res.status === 'success') {
        showSuccessToast(`${t('exerciseDeletedSuccessfully')} ${row.filename}`);
        fetchSearchResults();
      } else {
        showErrorToast(res.message || `${t('errorDeletingExercise')} ${row.filename}`);
      }
    } catch (err) {
      const msg = `${t('errorDeletingExercise')} '${row.filename}'`;
      showErrorToast(msg);
    }
  };

  const handleDownload = async (row: any) => {
    try {
        const response = await newExerciseService.downloadExerciseFromAzure(row.exerciseName, row.id);

        const blob = new Blob([response], {
            type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = row.exerciseName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Download failed", error);
    }
  };

  const handleOpenChart = (chartImageBase64: string): void => {
    setChartImage(chartImageBase64);
    setOpenChartModal(true);
  };

  const handleCloseChart = (): void => {
    setOpenChartModal(false);
    setChartImage(null);
  };



  const columns = [
    {
      field: 'filename',
      headerName: t('exerciseName'),
      flex: 3,
      cellClassName: 'exercise-name-ltr'
    },
    {
      field: 'similarity_score',
      headerName: t('matchScore'),
      flex: 2
    },
    {
      field: 'status',
      headerName: t('status'),
      flex: 2,
      renderCell: (params: any) => (
        <span className={params.value === 'אושר' ? 'status-approved' : 'status-not-approved'}>
          {params.value}
        </span>
      )
    },
    {
      field: 'upload_date',
      headerName: t('uploadDate'),
      flex: 2
    },
    {
      field: 'actions',
      headerName: t('operations'),
      sortable: false,
      flex: 1,
      renderCell: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
          <IconButton className='action_btns download_btn' onClick={() => {
            setSelectedExerciseId(params.row.id);
            setDrawerOpen(true);
          }}>
            <img src={EditIcon} alt="Edit" />
          </IconButton>
          <IconButton className='action_btns download_btn' onClick={() => handleDownload(params.row)}>
            <img src={downloadIcon} alt="Download" />
          </IconButton>
          <IconButton className='action_btns' 
            onClick={() => {
              setConfirmationData({
                title: t('confirmDeleteExercise'),
                message: `${t('deleteExerciseConfirmation')} <strong>${params.row.filename}</strong>`,
                onConfirm: async () => {
                  setConfirmationLoading(true);
                  await handleDelete(params.row);
                  setConfirmationOpen(false);
                  setConfirmationLoading(false);
                },
              });
              setConfirmationOpen(true);
            }}
          >
            <img src={deleteIcon} alt="Delete" />
          </IconButton>
        </div>
      )
    }
  ];





  const activeFilters: any[] = [];

  return (
    <div className='dashboard-table'>
      <Box className='search_exercise'>
        <div className="image-upload-inline">
          <Paper
            variant="outlined"
            {...getRootProps()}
            className={`dropzone-inline ${selectedImage ? 'dropzone-inline--has-file' : ''}`}
          >
            <input {...getInputProps()} />
            <img src={plusIcon} alt="Plus Icon" />
            <Typography variant="body2" color="textSecondary">
              {isDragActive
                ? t('dropImageHere')
                : selectedImage
                ? selectedImage.name
                : t('uploadImageToSearch')}
            </Typography>
            {selectedImage && (
              <IconButton 
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveImage();
                }} 
                size="small" 
                className="remove-image-btn"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>
        </div>
        
        <Button
          text={t('searchSimilarExercises')}
          color="blue"
          onClick={handleSearchByImage}
          disabled={!selectedImage}
          loader={isSearching}
        />
      </Box>

      {uploadError && (
        <Alert severity="error" className="upload-error-message">
          {uploadError}
        </Alert>
      )}

      {activeFilters.length > 0 && (
        <div className='table_header'>
          <p className='filter_result_count'>
            <span>{rowCount}</span> {t('resultsFound')}
          </p>
          <div className='table_header_button'>
            {activeFilters.map((filter) => (
              <div className='single_header_button' key={filter.key}>
                <span>{filter.label}:</span>
                <ul>
                  <li>
                    <span>{filter.value}</span>
                    <button onClick={() => {}}>
                      <img src={crossIcon} alt="Cross Icon" />
                    </button>
                  </li>
                </ul>
              </div>
            ))}
            <button className='delete_filter_btn' onClick={() => {}}>
              <img src={trashIcon} alt="Trash Icon" />
              <span>{t('deleteFilters')}</span>
            </button>
          </div>
        </div>
      )}

      <CustomDataGrid
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        loading={loading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ExerciseDetailsDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedExerciseId(null);
        }}
        exerciseId={selectedExerciseId}
        mode="edit"
        onUpdateSuccess={fetchSearchResults}
      />

      <Modal open={openChartModal} onClose={handleCloseChart}>
        <Box className="chart-modal-box">
          <button
            className="chart-modal-close"
            onClick={handleCloseChart}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={
              chartImage
                ? chartImage.startsWith('data:')
                  ? chartImage
                  : `data:image/png;base64,${chartImage}`
                : ''
            }
            alt="Chart"
            className="chart-modal-img"
          />
        </Box>
      </Modal>
      
      <ConfirmationModal
        open={confirmationOpen}
        title={confirmationData.title}
        message={confirmationData.message}
        loading={confirmationLoading}
        onConfirm={confirmationData.onConfirm}
        onCancel={() => setConfirmationOpen(false)}
        confirmText={t('yes')}
        cancelText={t('no')}
      />
    </div>
  );
};

export default SearchExercise; 